import { inject, provideEnvironmentInitializer } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, type Route, Router } from '@angular/router';

import { NEVER } from 'rxjs';

import { injectTranslate } from '@core/i18n-legacy';
import { type DialogConfigOptions, DialogWidth } from '@core/user-interface-legacy';
import { AskHRService } from '@tellent-ask-hr/core-logic';
import { TellentUserRequestsViewComponent } from '@tellent-ask-hr/core-smart-ui';
import {
  AskTemplateNotificationsService,
  CreateTicketDialogComponent,
  CreateTicketDialogService,
  TellentAskTemplatesViewComponent,
  TellentUserTemplatesViewComponent,
} from '@tellent-ask-hr/templates-smart-ui';
import {
  TellentAskTicketsViewComponent,
  TellentUserTicketsViewComponent,
  TicketDialogComponent,
  TicketDialogService,
  type TicketEmptyStateData,
} from '@tellent-ask-hr/tickets-smart-ui';

export const ROUTES = [
  {
    path: 'ask-hr',
    children: [
      {
        path: 'tickets',
        component: TellentAskTicketsViewComponent,
        canActivate: [
          () =>
            inject(AskHRService).canAccessTicketsPage() ||
            new RedirectCommand(inject(Router).parseUrl('/ask-hr/my/tickets')),
        ],
      },
      {
        path: 'forms',
        component: TellentAskTemplatesViewComponent,
        canActivate: [
          () =>
            inject(AskHRService).canAccessFormsPage() ||
            new RedirectCommand(inject(Router).parseUrl('/ask-hr/my/tickets')),
        ],
      },
      {
        path: 'my',
        component: TellentUserRequestsViewComponent,
        children: [
          { path: 'forms', component: TellentUserTemplatesViewComponent },
          { path: 'tickets', component: TellentUserTicketsViewComponent },
          { path: '**', redirectTo: 'tickets' },
        ],
      },
      { path: '**', redirectTo: 'my/tickets' },
    ],
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr/create-ticket/:templateId',
    component: CreateTicketDialogComponent,
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (route: ActivatedRouteSnapshot) =>
        inject(CreateTicketDialogService).resolveDialogData(route),
    },
    canDeactivate: [() => inject(CreateTicketDialogService).canDeactivateGuard()],
    outlet: 'dialog',
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr/ticket/:ticketId',
    component: TicketDialogComponent,
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (route: ActivatedRouteSnapshot) =>
        inject(TicketDialogService).resolveDialogData(route),
    },
    outlet: 'dialog',
  },
  {
    path: 'ask-hr/ticket-not-found',
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (): TicketEmptyStateData => {
        const t = injectTranslate();
        return {
          title: t('tlc.ask_hr.request_dialog.cant_view_title'),
          desc: t('tlc.ask_hr.request_dialog.cant_view_desc'),
          icon: 'local-activity',
        };
      },
    },
    loadComponent: () =>
      import('@tellent-ask-hr/tickets-smart-ui').then(m => m.TicketEmptyStateComponent),
    outlet: 'dialog',
  },
  {
    path: '**',
    canActivate: [() => NEVER],
    loadComponent: () => NEVER,
  },
] satisfies Route[];
